import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Alert, CircularProgress, Typography, Link } from '@mui/material';
import { getToken, removeToken } from '../../../app/auth';
import { useTranslation } from '../../../app/LanguageContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const WORKSPACE_STORAGE_KEY = 'eagle_workspace_id';

function readWorkspaceId() {
  try {
    return localStorage.getItem(WORKSPACE_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Full-page viewer: URL stays /orders/:orderId/payments/:paymentId/receipt (SPA).
 * Fetches binary from API with auth and shows it in an iframe via blob URL.
 */
const OrderPaymentReceiptPage = () => {
  const { orderId, paymentId } = useParams();
  const { translate } = useTranslation();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      setObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });

      const token = getToken();
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const ws = readWorkspaceId();
      if (ws) {
        headers['X-Workspace-ID'] = ws;
      }

      try {
        const response = await fetch(`${API_URL}/orders/${orderId}/payments/${paymentId}/receipt`, {
          method: 'GET',
          headers,
        });
        if (response.status === 401) {
          removeToken();
          window.location.href = '/login';
          return;
        }
        if (!response.ok) {
          const text = await response.text();
          let msg = text;
          try {
            const j = text ? JSON.parse(text) : {};
            msg = j.error || j.message || text;
          } catch (_) {
            /* keep text */
          }
          if (!cancelled) {
            setError(msg || translate('common.error'));
            setLoading(false);
          }
          return;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setObjectUrl(url);
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || translate('common.error'));
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
      setObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [orderId, paymentId, translate]);

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 560, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body2">
          <Link component={RouterLink} to={`/orders/${orderId}/edit`} underline="hover">
            {translate('orders.back_to_order')}
          </Link>
        </Typography>
      </Box>
    );
  }

  if (loading || !objectUrl) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Link component={RouterLink} to={`/orders/${orderId}/edit`} underline="hover" variant="body2">
          ← {translate('orders.back_to_order')}
        </Link>
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          /orders/{orderId}/payments/{paymentId}/receipt
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <iframe
          title={translate('orders.payment_receipt') || 'Comprovante'}
          src={objectUrl}
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      </Box>
    </Box>
  );
};

export default OrderPaymentReceiptPage;
